import { ChangeDetectionStrategy, Component, input, model, output, computed, inject } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { translation } from '@cue/translations';
import { ReactiveFormsModule } from '@angular/forms';
import { TextboxComponent } from '@cue/assist/ui/inputs';
import { LoadingComponent } from '@cue/loading';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  cueAppsSort,
  cueArrowRight,
  cueClose,
  cueFilter,
  cueInfo,
  cueInfoNoCircle,
  cueMarker,
  cueQrCode,
  cueSignInAlt,
  cueSearch,
  cueArrowFullLeft,
  IconComponent,
} from '@cue/ui/icons';
import { ImageWidthPipe, LocalizedStringPipe, DragScrollDirective } from '@cue/utilities';
import { ButtonComponent, ToggleButtonComponent } from '@cue/ui';
import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DynamicScrollSizeDirective } from '@cue/virtual-scroll';
import { localized } from '@cue/translate';
import {
  Area,
  INavigationResourceItem,
  ListType,
  NavigationBasicInfo,
  NavigationNearestItem,
  ResourcePoiIdentificationCrate,
  StartTargetSelectionModeEnum,
} from '@cue/data';
import { DecimalPipe, NgStyle, SlicePipe } from '@angular/common';
import { rxResource } from '@angular/core/rxjs-interop';
import { CustomUnsafeSanitizePipe } from '@cue/security';
import { BasicListDataSourceFactory } from './basic-list-datasource';
import { StartTargetFilterComponent, TargetFilterData } from '../start-target-filter/start-target-filter.component';
import { AreaToHierarchyPipe } from '../../pipes/area-to-hierarchy.pipe';
import { PoiBooleanFilterPipe } from '../../pipes/poi-boolean-filter.pipe';
import { CategoryListResourcesOnlyPipe } from '../../pipes/category-list-resources-only.pipe';
import { CategoryListCategoriesOnlyPipe } from '../../pipes/category-list-categories-only.pipe';
import { SearchResultPipe } from '../../pipes/search-result.pipe';
import { CanShowPoisPipe } from '../../pipes/can-show-pois.pipe';
import { BaseSelection } from '../base-selection';
import { FilterStartTargetResourceNearestPipe } from '../../pipes/filter-start-target-resource-nearest.pipe';

export enum SearchTypeEnum {
  Alhabetical,
  Nearest,
}

@Component({
  selector: 'cue-target-selection',
  templateUrl: './target-selection.component.html',
  styleUrls: ['./target-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TextboxComponent,
    TranslocoPipe,
    ReactiveFormsModule,
    LoadingComponent,
    IconComponent,
    ToggleButtonComponent,
    ButtonComponent,
    ImageWidthPipe,
    StartTargetFilterComponent,
    AreaToHierarchyPipe,
    PoiBooleanFilterPipe,
    CategoryListResourcesOnlyPipe,
    CategoryListCategoriesOnlyPipe,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    DynamicScrollSizeDirective,
    LocalizedStringPipe,
    SearchResultPipe,
    CustomUnsafeSanitizePipe,
    CanShowPoisPipe,
    DragScrollDirective,
    NgStyle,
    SlicePipe,
    DecimalPipe,
    FilterStartTargetResourceNearestPipe,
  ],
  animations: [
    trigger('slideInTop', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-50%)' }),
        animate('300ms', style({ opacity: 1, transform: 'translateY(0%)' })),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'translateY(0%)' }),
        animate('300ms', style({ opacity: 0, transform: 'translateY(-50%)' })),
      ]),
    ]),
    trigger('opacity', [
      transition(':enter', [style({ opacity: 0 }), animate('300ms', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('300ms', style({ opacity: 0 }))]),
    ]),
    trigger('scale', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0)' }),
        animate(
          '300ms',
          style({
            opacity: 1,
            transform: 'scale(1)',
          })
        ),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'scale(1)' }),
        animate(
          '300ms',
          style({
            opacity: 0,
            transform: 'scale(0)',
          })
        ),
      ]),
    ]),
  ],
})
export class TargetSelectionComponent extends BaseSelection {
  showNavigationFilter = model<boolean>(false);
  protected listType = ListType;
  protected translation = translation;
  protected ICONS = {
    cueQrCode,
    cueArrowRight,
    cueFilter,
    cueMarker,
    cueAppsSort,
    cueClose,
    cueSignInAlt,
    cueInfo,
    cueInfoNoCircle,
    cueSearch,
    cueArrowFullLeft,
  };

  isKioskMode = computed(() => {
    return !!this.kioskId() || !!this.appId();
  });

  deploymentId = input<string>();

  availableAreaList = input<Area[]>();
  startData = input<{
    qrCodeId?: string;
    resourceId?: number;
  }>();
  targetResourceName = model<string>();
  targetResourceId = input<number>();
  targetQrCodeId = input<string>();

  targetSelected = output<INavigationResourceItem>();
  requestOpenItemDetail = output<ResourcePoiIdentificationCrate>();
  requestCloseTargetSelection = output<void>();

  nearestItemsOverlaysIds: number[] = [];

  protected selectedSearchType: SearchTypeEnum = SearchTypeEnum.Alhabetical;
  protected searchTypeEnum = SearchTypeEnum;

  factory = inject(BasicListDataSourceFactory);
  basicListDataSource = rxResource({
    params: () => {
      const deploymentId = this.deploymentId();
      const kioskId = this.kioskId();
      const appId = this.appId();
      const searchString = this.searchSignal();
      const selectedCategoryIdList = this.selectedCategoryIdList();
      const selectedAreaIdList = this.selectedAreaIdList();
      const selectedResourceTypeIdList = this.selectedResourceTypeIdList();
      const selectedLanguage = this.selectedLanguage();

      return {
        kioskId: kioskId,
        appId: appId,
        deploymentId: deploymentId,
        languageCode: selectedLanguage,
        searchString: searchString,
        selectedAreaIds: selectedAreaIdList,
        selectedCategoryIdList: selectedCategoryIdList,
        selectedResourceTypeIds: selectedResourceTypeIdList,
      };
    },
    stream: ({ params }) => this.factory.create(params),
  });

  navigateToResource(
    resourceDetail: NavigationBasicInfo | NavigationNearestItem,
    mouseEvent: MouseEvent,
    isFromNearestList: boolean
  ) {
    this.navigateDEP(resourceDetail, mouseEvent, isFromNearestList);
  }

  protected nearestItemRemoveOverlay(resourceId: number) {
    this.nearestItemsOverlaysIds.splice(this.nearestItemsOverlaysIds.findIndex((x) => x === resourceId));
  }

  navigate(
    resourceDetail: NavigationNearestItem | NavigationBasicInfo,
    mouseEvent: MouseEvent,
    isFromNearest: boolean
  ) {
    if (resourceDetail.qrCodes?.length === 1 && resourceDetail.qrCodes[0].notNavigationPoint) {
      return;
    }
    if (resourceDetail.qrCodes && resourceDetail.qrCodes.filter((x) => !x.notNavigationPoint).length > 1) {
      const overlayIdx = this.overlaysIds().findIndex((x) => x === resourceDetail.resourceId);

      if (overlayIdx > -1) {
        this.removeOverlay(resourceDetail.resourceId);
      } else {
        this.addOverlay(resourceDetail.resourceId);
        return;
      }
    }
    this.navigateToResource(resourceDetail, mouseEvent, isFromNearest);
  }

  protected navigateDEP(
    resourceDetail: NavigationBasicInfo | NavigationNearestItem,
    event: MouseEvent,
    isFromNearestList: boolean
  ) {
    event.stopPropagation();

    let qrCodeIdToNavigate = null;

    if (resourceDetail.resourceId == null) {
      if (resourceDetail.qrCodeId) {
        qrCodeIdToNavigate = resourceDetail.qrCodeId;
      } else if ((resourceDetail as unknown as NavigationBasicInfo).qrCodes?.length == 1) {
        qrCodeIdToNavigate = (resourceDetail as unknown as NavigationBasicInfo).qrCodes[0].qrCodeId;
      }
    }

    this.targetSelected.emit({
      areaId: resourceDetail.areaId,
      qrCodeId: qrCodeIdToNavigate,
      name: localized(resourceDetail.name, this.translocoService),
      displayNameForApp: localized(resourceDetail.displayNameForApp, this.translocoService),
      resourceId: resourceDetail.resourceId,
      targetSelectionMode: isFromNearestList
        ? StartTargetSelectionModeEnum.SelectedFromNearestList
        : StartTargetSelectionModeEnum.SelectedFromList,
    });
    this.targetResourceName.set(
      localized(resourceDetail.displayNameForApp, this.translocoService) ||
        localized(resourceDetail.name, this.translocoService)
    );
  }

  navigateToSpecificQR(resourceDetail: NavigationBasicInfo | NavigationNearestItem, qrCode: string, event: MouseEvent) {
    event.stopPropagation();
    const qrPointName = resourceDetail.qrCodes.find((x) => x.qrCodeId === qrCode)?.name;

    this.targetSelected.emit({
      areaId: resourceDetail.areaId,
      qrCodeId: qrCode,
      name: qrPointName,
      displayNameForApp: qrPointName,
      resourceId: null,
      targetSelectionMode: StartTargetSelectionModeEnum.SelectedFromList,
    });

    this.targetResourceName.set(localized(qrPointName, this.translocoService));
  }

  override updateData(filter: TargetFilterData) {
    super.updateData(filter);
    if (
      filter.selectedResourceTypeIdList.length === 0 &&
      filter.selectedCategoryIdList.length === 0 &&
      filter.selectedAreaIdList.length === 0
    ) {
      this.selectedSearchType = SearchTypeEnum.Alhabetical;
    }
  }

  override clearCategoryFilter() {
    super.clearCategoryFilter();
    this.selectedSearchType = SearchTypeEnum.Alhabetical;
  }

  override clearTypeFilter() {
    super.clearTypeFilter();
    this.selectedSearchType = SearchTypeEnum.Alhabetical;
  }

  resetFilters() {
    this.selectedAreaIdList.set([]);
    this.selectedCategoryIdList.set([]);
    this.selectedResourceTypeIdList.set([]);
    this.showNavigationFilter.set(false);
    this.selectedSearchType = SearchTypeEnum.Alhabetical;
    this.search.patchValue('');

    const requestedElement = document.getElementById('startPosition');
    if (requestedElement) {
      requestedElement.scrollIntoView();
    }
  }

  openItemDetail(detail: NavigationBasicInfo, mouseEvent: MouseEvent) {
    mouseEvent.stopPropagation();
    this.requestOpenItemDetail.emit({
      resourceId: detail.resourceId,
      qrCodeId: detail.qrCodes.length === 1 ? detail.qrCodes[0].qrCodeId : detail.qrCodeId,
    });
    this.closeQrPopup.emit();
  }

  openNearestItemDetail(detail: NavigationNearestItem, mouseEvent: MouseEvent) {
    mouseEvent.stopPropagation();
    this.requestOpenItemDetail.emit({
      resourceId: detail.resourceId,
      qrCodeId: detail.qrCodes.length === 1 ? detail.qrCodes[0].qrCodeId : detail.qrCodeId,
    });
  }

  protected changeShowFilter(showFilter: boolean) {
    this.showNavigationFilter.set(showFilter);
    this.closeQrPopup.emit();
  }

  closeQrPopup = output<void>();

  toggleShowNearest(item: NavigationNearestItem, event: Event) {
    event.stopPropagation();
    item.searchResultIsShown = !item.searchResultIsShown;
  }

  nearestPointListParams = computed(() => {
    return {
      startData: this.startData(),
      selectedCategoryIdList: this.selectedCategoryIdList(),
      selectedAreaIdList: this.selectedAreaIdList(),
      selectedResourceTypeIdList: this.selectedResourceTypeIdList(),
      kioskId: this.kioskId(),
      appId: this.appId(),
    };
  });

  nearestPointListData = rxResource({
    params: () => this.nearestPointListParams(),
    stream: ({ params }) =>
      this.navigationService.getNavigationNearestPointList({
        start: params.startData,
        categoryIdList: params.selectedCategoryIdList,
        areaIdList: params.selectedAreaIdList,
        resourceTypeIdList: params.selectedResourceTypeIdList,
        kioskId: params.kioskId,
        appId: params.appId,
      }),
  });

  nearestOfSortData = computed(() => {
    // keep  it here
    const selectedLanguage = this.selectedLanguage();
    if (this.nearestPointListData.isLoading()) {
      return { loading: true, data: [] };
    }
    if (this.nearestPointListData.error()) {
      return { loading: false, data: [] };
    }
    const searchString = this.searchSignal();
    const selectedCategoryIdList = this.selectedCategoryIdList();
    const normalizedSearchText = this.normalizeText(searchString);
    return {
      loading: this.nearestPointListData.isLoading(),
      data: this.nearestPointListData
        .value()
        .filter(
          (x) =>
            (searchString.length == 0 ||
              this.normalizeText(localized(x.name, this.translocoService, false)).indexOf(normalizedSearchText) !==
                -1 ||
              (localized(x.resourceTypeName, this.translocoService) &&
                this.normalizeText(localized(x.resourceTypeName, this.translocoService, false)).indexOf(
                  normalizedSearchText
                ) !== -1) ||
              (localized(x.resourceDescription, this.translocoService) &&
                this.normalizeText(localized(x.resourceDescription, this.translocoService, false)).indexOf(
                  normalizedSearchText
                ) !== -1) ||
              (x.resourcePropertiesInfo?.length > 0 &&
                (x.resourcePropertiesInfo.filter(
                  (prop) =>
                    localized(prop.valueText, this.translocoService, false) &&
                    this.normalizeText(localized(prop.valueText, this.translocoService, false)).indexOf(
                      normalizedSearchText
                    ) !== -1
                )?.length > 0 ||
                  x.resourcePropertiesInfo.filter(
                    (prop) => prop.valueDecimal && (prop.valueDecimal + '').indexOf(normalizedSearchText) !== -1
                  )?.length > 0 ||
                  x.resourcePropertiesInfo.filter(
                    (prop) => prop.valueNumber && (prop.valueNumber + '').indexOf(normalizedSearchText) !== -1
                  )?.length > 0 ||
                  x.resourcePropertiesInfo.filter(
                    (prop) =>
                      localized(prop.name, this.translocoService, false) &&
                      this.normalizeText(localized(prop.name, this.translocoService, false)).indexOf(
                        normalizedSearchText
                      ) !== -1
                  )?.length > 0)) ||
              (x.displayNameForApp &&
                this.normalizeText(localized(x.displayNameForApp, this.translocoService, false)).indexOf(
                  normalizedSearchText
                ) !== -1) ||
              x.qrCodes.filter(
                (qr) =>
                  this.normalizeText(localized(qr.name, this.translocoService, false))?.indexOf(normalizedSearchText) >
                    -1 ||
                  this.normalizeText(localized(qr.mapName, this.translocoService, false))?.indexOf(
                    normalizedSearchText
                  ) > -1 ||
                  this.normalizeText(localized(qr.qrCategoryName, this.translocoService, false))?.indexOf(
                    normalizedSearchText
                  ) > -1 ||
                  qr.tags?.findIndex(
                    (x) =>
                      this.normalizeText(localized(x.name, this.translocoService, false)).indexOf(
                        normalizedSearchText
                      ) !== -1
                  ) > -1
              ).length > 0 ||
              x.tags.filter(
                (qr) =>
                  this.normalizeText(localized(qr.name, this.translocoService, false)).indexOf(normalizedSearchText) >
                  -1
              ).length > 0) &&
            (selectedCategoryIdList == null ||
              selectedCategoryIdList.length == 0 ||
              selectedCategoryIdList.filter((a) => x.qrCodes.filter((qr) => qr.qrCategoryCodeId == a).length > 0)
                .length > 0)
        )
        .map((d) => {
          return {
            ...d,
          };
        }),
    };
  });
}
